'use client';

import './map.css';

import Link from 'next/link';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { geoBounds, geoContains, geoNaturalEarth1, geoPath } from 'd3-geo';
import { select } from 'd3-selection';
import 'd3-transition';  // augments selection.prototype with .transition()
import { D3ZoomEvent, ZoomBehavior, ZoomTransform, zoom as d3zoom, zoomIdentity } from 'd3-zoom';
import { feature } from 'topojson-client';
import type { FeatureCollection, Geometry } from 'geojson';
import worldData from 'world-atlas/countries-110m.json';

import { api } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import { MapPost } from '@/types';
import { numericToAlpha2 } from './country-codes';

interface CountryProps {
  name: string;
}

const ZOOM_MIN = 1;
const ZOOM_MAX = 12;

// Decode the TopoJSON once at module load — it never changes.
// world-atlas ships ISO country IDs as `feature.id` and the English short name
// as `feature.properties.name`. We use the id for grouping (stable) and the
// name for display.
const WORLD = feature(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  worldData as any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (worldData as any).objects.countries,
) as unknown as FeatureCollection<Geometry, CountryProps>;

// Fixed cartographic viewBox — SVG scales to its container via CSS.
// Natural Earth 1 looks more "drawn" than Mercator and avoids the polar warp
// that makes Mercator feel datey/web-mappy.
const VB_W = 1000;
const VB_H = 520;
const PROJECTION = geoNaturalEarth1().fitSize([VB_W, VB_H], WORLD);
const PATH = geoPath(PROJECTION);

/** Linear-interp dark slate → cyan glow. Quadratic ease gives high-density
 *  countries a more dramatic glow without flattening the low end. */
function colorFor(count: number, max: number): string {
  if (count === 0) return '#0f172a';  // slate-900-ish baseline
  const t = max > 0 ? Math.pow(Math.min(1, count / max), 0.55) : 0;
  // base: #0f172a, peak: #38bdf8
  const r = Math.round(15 + (56 - 15) * t);
  const g = Math.round(23 + (189 - 23) * t);
  const b = Math.round(42 + (248 - 42) * t);
  return `rgb(${r}, ${g}, ${b})`;
}

interface CountryAgg {
  id: string;
  name: string;
  posts: MapPost[];
}

// Cap dots per country so a megacity doesn't drown the map in glitter.
// Anything past this is implied by the country fill colour anyway.
const MAX_DOTS_PER_COUNTRY = 30;

/** Tiny seeded PRNG (mulberry32). Same seed → same sequence, so dot positions
 *  stay put across re-renders / zoom (they don't reshuffle every frame). */
function mulberry32(seed: number) {
  let t = seed >>> 0;
  return () => {
    t = (t + 0x6D2B79F5) >>> 0;
    let r = t;
    r = Math.imul(r ^ (r >>> 15), r | 1);
    r ^= r + Math.imul(r ^ (r >>> 7), r | 61);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

function hashStr(s: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  }
  return h >>> 0;
}

/** Generate up to N points uniformly inside a country polygon via rejection
 *  sampling against its lon/lat bounding box. Cheap and good enough — 110m
 *  countries are simple enough that hit rate is high (worst case ~30% for
 *  spindly countries, fine within our retry budget). */
function sampleDots(
  feat: GeoJSON.Feature,
  count: number,
  rand: () => number,
): Array<[number, number]> {
  if (count <= 0) return [];
  const [[minLng, minLat], [maxLng, maxLat]] = geoBounds(feat);
  const out: Array<[number, number]> = [];
  // Hard cap on tries so a degenerate polygon can never spin forever.
  const maxTries = count * 30;
  let tries = 0;
  while (out.length < count && tries < maxTries) {
    tries++;
    const lng = minLng + rand() * (maxLng - minLng);
    const lat = minLat + rand() * (maxLat - minLat);
    if (geoContains(feat, [lng, lat])) out.push([lng, lat]);
  }
  return out;
}

export default function MapView() {
  const { locale, t } = useI18n();

  // `Intl.DisplayNames` localizes ISO 3166-1 alpha-2 region codes into the
  // active UI language (browser-native, no extra data needed). Falls back
  // gracefully to the English name from world-atlas when the code is unknown
  // or the API isn't available (very old browsers).
  const regionNames = useMemo(() => {
    try {
      return new Intl.DisplayNames([locale], { type: 'region' });
    } catch {
      return null;
    }
  }, [locale]);

  const localizedName = useCallback((numericId: string, fallback: string): string => {
    const a2 = numericToAlpha2(numericId);
    if (a2 && regionNames) {
      try {
        const name = regionNames.of(a2);
        if (name) return name;
      } catch { /* fall through */ }
    }
    return fallback;
  }, [regionNames]);

  const [posts, setPosts] = useState<MapPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [hover, setHover] = useState<{ id: string; x: number; y: number } | null>(null);
  // Drives the transform on the inner <g>. Updated by d3-zoom on every gesture.
  const [transform, setTransform] = useState<ZoomTransform>(zoomIdentity);
  const svgRef = useRef<SVGSVGElement>(null);
  const zoomRef = useRef<ZoomBehavior<SVGSVGElement, unknown> | null>(null);

  useEffect(() => {
    api.map.posts()
      .then(setPosts)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Failed to load map'))
      .finally(() => setLoading(false));
  }, []);

  // Wire d3-zoom on the SVG. We don't apply the transform via d3 directly —
  // we mirror it into React state so the country `<g>` rerenders with the
  // new transform attribute. This keeps the zoom interaction native (smooth
  // pinch/wheel) while letting React stay the source of truth for paint.
  useEffect(() => {
    if (!svgRef.current) return;
    const svg = select<SVGSVGElement, unknown>(svgRef.current);
    const z = d3zoom<SVGSVGElement, unknown>()
      .scaleExtent([ZOOM_MIN, ZOOM_MAX])
      // Restrict pan so the world can't be flung off-screen entirely. Half a
      // viewBox on every side is generous enough to peek past the edges.
      .translateExtent([[-VB_W * 0.5, -VB_H * 0.5], [VB_W * 1.5, VB_H * 1.5]])
      .on('zoom', (e: D3ZoomEvent<SVGSVGElement, unknown>) => {
        setTransform(e.transform);
      });
    zoomRef.current = z;
    svg.call(z);
    // Disable d3's default double-click-to-zoom — we want clicks to select a
    // country, and a stray double-click would yank the camera around.
    svg.on('dblclick.zoom', null);
    return () => { svg.on('.zoom', null); };
  }, []);

  const zoomBy = useCallback((factor: number) => {
    if (!svgRef.current || !zoomRef.current) return;
    select(svgRef.current).transition().duration(220).call(zoomRef.current.scaleBy, factor);
  }, []);
  const zoomReset = useCallback(() => {
    if (!svgRef.current || !zoomRef.current) return;
    select(svgRef.current).transition().duration(280).call(zoomRef.current.transform, zoomIdentity);
  }, []);

  // Per-country aggregation. We do point-in-polygon ourselves with
  // d3.geoContains rather than asking the backend to reverse-geocode at write
  // time — keeps the schema simpler and lets us re-bucket if we ever swap the
  // country atlas.
  const { byCountry, max } = useMemo(() => {
    const map = new Map<string, CountryAgg>();
    for (const f of WORLD.features) {
      const id = String(f.id ?? f.properties.name);
      map.set(id, { id, name: f.properties.name, posts: [] });
    }
    for (const p of posts) {
      const point: [number, number] = [p.user.location.lng, p.user.location.lat];
      // First feature whose polygon contains this point wins. World atlas
      // features are non-overlapping land polygons so this is unambiguous.
      const f = WORLD.features.find((feat) => geoContains(feat, point));
      if (!f) continue;
      const id = String(f.id ?? f.properties.name);
      map.get(id)?.posts.push(p);
    }
    let m = 0;
    for (const agg of map.values()) if (agg.posts.length > m) m = agg.posts.length;
    return { byCountry: map, max: m };
  }, [posts]);

  // Pre-compute random in-country dots — one batch per render-of-byCountry,
  // not per zoom/hover. Memoization + seeded RNG means positions are stable
  // across re-renders so the eye doesn't see them shimmer.
  const dots = useMemo(() => {
    const out: Array<{ id: string; cx: number; cy: number }> = [];
    for (const f of WORLD.features) {
      const id = String(f.id ?? f.properties.name);
      const agg = byCountry.get(id);
      const n = agg?.posts.length ?? 0;
      if (n === 0) continue;
      const cap = Math.min(n, MAX_DOTS_PER_COUNTRY);
      const rand = mulberry32(hashStr(id) ^ n);
      const samples = sampleDots(f, cap, rand);
      for (let i = 0; i < samples.length; i++) {
        const xy = PROJECTION(samples[i]);
        if (xy) out.push({ id: `${id}-${i}`, cx: xy[0], cy: xy[1] });
      }
    }
    return out;
  }, [byCountry]);

  const active = activeId ? byCountry.get(activeId) ?? null : null;
  const hoverAgg = hover ? byCountry.get(hover.id) ?? null : null;

  return (
    <div className="pm-map-root">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        preserveAspectRatio="xMidYMid meet"
        className="pm-map-svg"
        role="img"
        aria-label="World map of prompt creators by country"
      >
        {/* Soft radial glow backdrop — gives the dark slate base a subtle vignette */}
        <defs>
          <radialGradient id="pm-bg" cx="50%" cy="50%" r="65%">
            <stop offset="0%" stopColor="#0b1220" />
            <stop offset="100%" stopColor="#05080d" />
          </radialGradient>
          <filter id="pm-glow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="2.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <rect width={VB_W} height={VB_H} fill="url(#pm-bg)" />

        {/* Everything inside this <g> pans + zooms together. Outside the
            group: only the static background rect. The transform comes from
            d3-zoom, mirrored into React state above. */}
        <g transform={transform.toString()}>
          {/* Countries */}
          <g>
            {WORLD.features.map((f) => {
              const id = String(f.id ?? f.properties.name);
              const agg = byCountry.get(id);
              const count = agg?.posts.length ?? 0;
              const fill = colorFor(count, max);
              const isActive = activeId === id;
              const isHover = hover?.id === id;
              return (
                <path
                  key={id}
                  d={PATH(f) ?? ''}
                  fill={fill}
                  stroke={isActive || isHover ? '#fde68a' : 'rgba(255,255,255,0.06)'}
                  strokeWidth={isActive ? 1.2 : 0.5}
                  // Keep border weight + dot size visually constant while
                  // zooming — otherwise borders get fat at high zoom and
                  // dots become huge blobs.
                  vectorEffect="non-scaling-stroke"
                  style={{
                    cursor: count > 0 ? 'pointer' : 'default',
                    filter: count > 0 && (isHover || isActive) ? 'url(#pm-glow)' : undefined,
                    transition: 'stroke 0.15s, stroke-width 0.15s',
                  }}
                  onMouseEnter={(e) => {
                    const rect = svgRef.current?.getBoundingClientRect();
                    if (!rect) return;
                    setHover({ id, x: e.clientX - rect.left, y: e.clientY - rect.top });
                  }}
                  onMouseMove={(e) => {
                    const rect = svgRef.current?.getBoundingClientRect();
                    if (!rect) return;
                    setHover({ id, x: e.clientX - rect.left, y: e.clientY - rect.top });
                  }}
                  onMouseLeave={() => setHover((h) => (h?.id === id ? null : h))}
                  onClick={() => {
                    if (count > 0) setActiveId(id);
                  }}
                />
              );
            })}
          </g>

          {/* Density dots — sprinkled randomly inside each country. Count =
              prompts in that country, capped at MAX_DOTS_PER_COUNTRY so a
              hotspot doesn't drown the canvas in glitter. Seeded RNG per
              country (hash of country id) so positions are stable. */}
          <g pointerEvents="none">
            {dots.map((d) => (
              <circle
                key={d.id}
                cx={d.cx}
                cy={d.cy}
                // Inverse-scale so the dot stays ≈1.6px no matter the zoom.
                r={1.6 / transform.k}
                fill="#fde68a"
                opacity={0.9}
              />
            ))}
          </g>
        </g>
      </svg>

      {/* Zoom controls — bottom-right glassmorphism stack */}
      <div className="pm-zoom" role="group" aria-label={t('map.title')}>
        <button type="button" onClick={() => zoomBy(1.6)} aria-label={t('map.zoom_in')}
          disabled={transform.k >= ZOOM_MAX - 0.001}>
          <span className="material-symbols-outlined">add</span>
        </button>
        <button type="button" onClick={() => zoomBy(1 / 1.6)} aria-label={t('map.zoom_out')}
          disabled={transform.k <= ZOOM_MIN + 0.001}>
          <span className="material-symbols-outlined">remove</span>
        </button>
        <button type="button" onClick={zoomReset} aria-label={t('map.zoom_reset')}
          disabled={transform.k === 1 && transform.x === 0 && transform.y === 0}>
          <span className="material-symbols-outlined">filter_center_focus</span>
        </button>
      </div>

      {/* HUD — corner overlay */}
      <div className="pm-hud">
        <div className="pm-hud-title">
          <span className="material-symbols-outlined">public</span>
          {t('map.title')}
        </div>
        <div className="pm-hud-stats">
          {loading
            ? t('common.loading')
            : error
              ? `${t('common.error_generic')}: ${error}`
              : t('map.stats', {
                  posts: posts.length,
                  countries: [...byCountry.values()].filter((c) => c.posts.length > 0).length,
                })}
        </div>
      </div>

      {/* Density legend */}
      {max > 0 && (
        <div className="pm-legend" aria-hidden>
          <span className="pm-legend-label">{t('map.legend_less')}</span>
          <span className="pm-legend-bar" />
          <span className="pm-legend-label">{t('map.legend_more', { max })}</span>
        </div>
      )}

      {/* Hover tooltip — follows mouse */}
      {hover && hoverAgg && (
        <div
          className="pm-tooltip"
          style={{ left: hover.x + 14, top: hover.y + 14 }}
        >
          <div className="pm-tooltip-name">{localizedName(hoverAgg.id, hoverAgg.name)}</div>
          <div className="pm-tooltip-count">
            {hoverAgg.posts.length === 0
              ? t('map.no_prompts')
              : hoverAgg.posts.length === 1
                ? t('map.one_prompt')
                : t('map.n_prompts', { count: hoverAgg.posts.length })}
          </div>
        </div>
      )}

      {/* Side panel — slides in from the right when a country is clicked */}
      {active && (
        <aside className="pm-panel" aria-label={localizedName(active.id, active.name)}>
          <header className="pm-panel-head">
            <div className="pm-panel-user" style={{ alignItems: 'flex-start' }}>
              <span className="pm-panel-avatar pm-panel-avatar-fallback" aria-hidden>
                <span className="material-symbols-outlined" style={{ fontSize: 22 }}>place</span>
              </span>
              <div style={{ minWidth: 0 }}>
                <div className="pm-panel-name">{localizedName(active.id, active.name)}</div>
                <div className="pm-panel-loc">
                  <span>
                    {active.posts.length === 1
                      ? t('map.one_prompt')
                      : t('map.n_prompts', { count: active.posts.length })}
                  </span>
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setActiveId(null)}
              aria-label={t('map.close_panel')}
              className="pm-panel-close"
            >
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>close</span>
            </button>
          </header>

          <div className="pm-panel-body">
            {active.posts.map((p) => (
              <Link key={p.id} href={`/prompt/${p.id}`} className="pm-card">
                {p.cover_image_url ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img src={p.cover_image_url} alt="" className="pm-card-cover" />
                ) : (
                  <div className="pm-card-cover pm-card-cover-empty">
                    <span className="material-symbols-outlined">draft</span>
                  </div>
                )}
                <div className="pm-card-body">
                  <div className="pm-card-title">{p.title}</div>
                  <div className="pm-card-cat">
                    <span style={{ color: '#cbd5e1' }}>@{p.user.username}</span>
                    {p.category && <span> · {p.category}</span>}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </aside>
      )}
    </div>
  );
}
